import React from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';

const KIND_LABELS = {
  stem_health: 'Stem health',
  mastering_readiness: 'Mastering readiness',
};

// "Stem health" or "Mastering readiness" for the backend's score_kind
export const scoreKindLabel = (kind) => KIND_LABELS[kind] || kind || '';

const SIZE = 128;
const STROKE = 10; // leaves the 108px inner disc
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * The 1-10 score at the top of an analysis, as an amber progress ring. From V2 on, pass `previous`
 * (and `previousVersion`) to show "↑ from 6". Show the score's name next to the ring, not in it.
 * The ring is never coral, even for low scores. With `animateIn` (a score that just arrived) the
 * ring fills past its mark and settles, the number pops in and a burst goes off; not under reduced motion.
 */
export const ScoreRing = ({ score, kind = 'stem_health', previous, previousVersion, animateIn = false }) => {
  const value = Math.min(10, Math.max(1, Math.round(score)));
  const delta = typeof previous === 'number' && previous !== value ? value - previous : 0;
  const label = scoreKindLabel(kind);
  const ariaLabel =
    `${label}: ${value} out of 10` +
    (delta ? `, ${delta > 0 ? 'up' : 'down'} from ${previous}${previousVersion ? ` in V${previousVersion}` : ''}` : '');

  return (
    <div role="img" aria-label={ariaLabel} className="relative w-score-ring h-score-ring flex-none">
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className="-rotate-90" aria-hidden="true">
        <circle cx={SIZE / 2} cy={SIZE / 2} r={RADIUS} fill="none" className="stroke-line" strokeWidth={STROKE} />
        <circle
          data-testid="score-ring-fill"
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          className={`stroke-amber ${animateIn ? 'animate-ring-fill motion-reduce:animate-none' : ''}`}
          strokeWidth={STROKE}
          strokeDasharray={`${(CIRCUMFERENCE * value) / 10} ${CIRCUMFERENCE}`}
        />
      </svg>
      {animateIn && (
        <svg data-testid="score-burst" viewBox="0 0 164 164" aria-hidden="true" className="absolute -inset-[18px] w-[164px] h-[164px] animate-burst motion-reduce:hidden">
          <g className="stroke-amber-hover" strokeWidth="3" strokeLinecap="round">
            <path d="M82 4v10M82 150v10M4 82h10M150 82h10M27 27l7 7M130 130l7 7M137 27l-7 7M34 130l-7 7" />
          </g>
        </svg>
      )}
      <div className="absolute inset-0 flex flex-col items-center justify-center" aria-hidden="true">
        <span className={`font-display text-display-score text-ink ${animateIn ? 'animate-score-pop motion-reduce:animate-none' : ''}`}>{value}</span>
        <span className="font-text text-caption text-muted">out of 10</span>
        {delta !== 0 && (
          // Progress is amber; a drop is quiet, since the headline and Fix next explain it
          <span className={`mt-space-1 inline-flex items-center gap-0.5 font-text text-label ${delta > 0 ? 'text-amber' : 'text-muted'}`}>
            {delta > 0 ? <ArrowUp size={12} strokeWidth={3} /> : <ArrowDown size={12} strokeWidth={3} />}
            from {previous}
          </span>
        )}
      </div>
    </div>
  );
};
